import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SNOOZE_MS = 24 * 60 * 60 * 1000;
const snoozed = (key) => Number(localStorage.getItem(key) || 0) > Date.now();
const snooze = (key) => localStorage.setItem(key, String(Date.now() + SNOOZE_MS));

export default function HealthPopups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [show, setShow] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user || user.role === "admin" && !user.impersonated_by) return;
    api.get("/health/integrations").then(({ data }) => {
      setHealth(data);
      if (data.issues.length && !snoozed("athanor_health_snooze")) setShow("health");
      else if (!data.process_configured && !snoozed("athanor_process_snooze")) setShow("process");
    }).catch((e) => console.error("health", e));
  }, [user]);

  if (!show || !health) return null;
  const close = () => { snooze(show === "health" ? "athanor_health_snooze" : "athanor_process_snooze"); setShow(null); };
  const report = async () => {
    setSending(true);
    try { const { data } = await api.post("/health/report", { codes: health.issues.map((i) => i.code) }); toast.success(data.message); close(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid={`popup-${show}`}>
      <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-[#111112] p-6 shadow-2xl">
        <div className="mb-3 flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-50">
            {show === "health" ? <><AlertTriangle className="h-5 w-5 text-amber-400" /> Qualcosa non funziona</> : <><CheckSquare className="h-5 w-5 text-[#C5A059]" /> Compila il tuo processo di vendita</>}
          </h3>
          <button onClick={close} data-testid="popup-close" className="text-slate-500 hover:text-slate-100"><X className="h-4 w-4" /></button>
        </div>
        {show === "health" ? (
          <>
            <div className="space-y-3">
              {health.issues.map((i) => (
                <div key={i.code} className="rounded-lg border border-zinc-800/70 p-3" data-testid={`issue-${i.code}`}>
                  <div className="text-sm font-semibold text-slate-100">{i.title}</div>
                  <div className="mt-0.5 text-xs text-slate-400">{i.detail}</div>
                  <div className="mt-1.5 text-xs text-slate-200"><span className="text-[#C5A059]">Come risolvere:</span> {i.fix}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button onClick={close} className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-slate-200">Ci penso io</button>
              <button onClick={report} disabled={sending} data-testid="popup-report" className="inline-flex items-center gap-2 rounded-md bg-[#C5A059] px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"><Send className="h-4 w-4" /> Segnala all'assistenza</button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-200">Senza il processo di vendita Hermes valuta i commerciali solo sui dati grezzi. Descrivere le fasi che ogni lead dovrebbe seguire (anche con l'aiuto di Hermes) rende score, report e suggerimenti molto più precisi.</p>
            <p className="mt-2 text-xs text-slate-500">Se chiudi, te lo ricorderemo tra 6 ore.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={close} className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-slate-200">Più tardi</button>
              <button onClick={() => { close(); navigate("/processo"); }} data-testid="popup-go-process" className="rounded-md bg-[#C5A059] px-4 py-2 text-sm font-semibold text-black">Compila ora</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
